import type { StreamDataHeader } from "../types/model.types";

export class RateModel {
  /**
   * The target playback rate for the last processed stream header.
   */
  playbackRate: number = 1;

  /**
   * The playback duration of the audio of the last processed stream header.
   */
  playbackDuration: number = 0;

  /**
   * When true, skips all rate adjustment calculations and uses 1.0 playback rate.
   * Used when source_duration_micro is unavailable from the server.
   * Set to false to re-enable rate adjustment.
   */
  public disabled: boolean = true;

  private readonly maxPlaybackRate: number = 1.2;
  private readonly minPlaybackRate: number = 0.9;
  private readonly sentenceFragmentDelay: number = 10.0;
  private readonly maxPlaybackRateHistory: number = 20;

  private prevEndTime: Date = new Date();
  private prevWasEndOfSentence: boolean = true;
  private inducedDelay: number = 0;
  private playbackRateHistories: Map<string, number[]> = new Map();
  private speakerAverages: Map<string, number> = new Map();

  public startTime: Date = new Date();
  public minStartTime: Date | null = null;

  /**
   * Processes the stream header to provide a desired playback rate and playback duration.
   * @param header - The stream header containing source and output durations in microseconds.
   */
  process(header: StreamDataHeader) {
    if (this.disabled) {
      const USEC_PER_SEC: number = 1000000;
      // Live transcription headers have no synthesized audio, so
      // output_duration_micro may be absent. Fall back to 0 (immediate text
      // display) — a NaN here serializes to null over the native bridge.
      const outputDuration: number = Number.isFinite(
        header.output_duration_micro,
      )
        ? header.output_duration_micro / USEC_PER_SEC
        : 0;
      this.playbackRate = 1;
      this.playbackDuration = outputDuration;
      this.startTime = new Date();
      this.minStartTime = null;
      this.prevEndTime = new Date(
        this.startTime.getTime() + outputDuration * 1000,
      );
      this.prevWasEndOfSentence = header.is_eos ?? true;
      return;
    }

    const receiveTime = new Date();
    const USEC_PER_SEC: number = 1000000;

    const outputDuration: number = header.output_duration_micro / USEC_PER_SEC;
    const sourceDuration: number = header.source_duration_micro / USEC_PER_SEC;

    let startTime: Date =
      this.prevEndTime > receiveTime ? this.prevEndTime : receiveTime;
    let startTimeDiff: number =
      (startTime.getTime() - receiveTime.getTime()) / 1000;
    let excessLatency: number = startTimeDiff;

    const isEndOfSentence = header.is_eos ?? true; // default to eos logic if not defined.
    const isStartOfSentenceFragment =
      this.prevWasEndOfSentence && !isEndOfSentence;

    const speakerLabel = header.speaker_label ?? "[Unknown]";
    const currentSpeakerAverage = this.speakerAverages.get(speakerLabel) ?? 1;
    const preferredDuration = outputDuration * currentSpeakerAverage;

    if (isStartOfSentenceFragment) {
      let requiredDelay = Math.max(
        this.sentenceFragmentDelay - sourceDuration,
        0,
      );

      const minEndTime = new Date(
        receiveTime.getTime() + this.sentenceFragmentDelay * 1000,
      );
      const preferredStartTime = new Date(
        minEndTime.getTime() - preferredDuration * 1000,
      );

      if (preferredStartTime >= startTime) {
        this.playbackRate = currentSpeakerAverage;
        startTime = preferredStartTime;
        startTimeDiff = (startTime.getTime() - receiveTime.getTime()) / 1000;
        excessLatency = 0;
      } else {
        const requiredDuration =
          (minEndTime.getTime() - startTime.getTime()) / 1000;
        this.playbackRate = this.calculatePlaybackRate(
          outputDuration,
          requiredDuration,
        );
      }

      this.minStartTime = startTime;
      this.inducedDelay = requiredDelay;
    } else {
      let recommendedDuration: number;
      if (isEndOfSentence) {
        // If the audio is at the end of a sentence, avoid adjusting the playback rate, unless the output duration
        // exceeds the source duration. Same applies to the start of a sentence fragment, since the audio will have been delayed intentionally.
        recommendedDuration = Math.min(sourceDuration, preferredDuration);
        this.inducedDelay = 0;
      } else {
        // If the audio is in the middle of a sentence, we want to have it match the source duration to avoid gaps.
        recommendedDuration = sourceDuration;
      }

      this.playbackRate = this.maxPlaybackRate;
      const realStartTimeDiff = Math.max(startTimeDiff - this.inducedDelay, 0);
      if (recommendedDuration > startTimeDiff) {
        const adjustedDuration = recommendedDuration - realStartTimeDiff;
        this.playbackRate = this.calculatePlaybackRate(
          outputDuration,
          adjustedDuration,
        );
      }

      this.minStartTime = null;
    }

    this.prevWasEndOfSentence = isEndOfSentence;
    this.startTime = startTime;
    this.playbackDuration = outputDuration / this.playbackRate;

    const matchSourcePlaybackRate = this.calculatePlaybackRate(
      outputDuration,
      sourceDuration,
    );

    // Get or create the history array for this speaker
    if (!this.playbackRateHistories.has(speakerLabel)) {
      this.playbackRateHistories.set(speakerLabel, []);
    }
    const speakerHistory = this.playbackRateHistories.get(speakerLabel)!;

    // Add the new playback rate to the speaker's history
    speakerHistory.push(matchSourcePlaybackRate);

    // Keep only the last 20 values for this speaker
    if (speakerHistory.length > this.maxPlaybackRateHistory) {
      speakerHistory.shift();
    }

    // Calculate the average of the last 20 values for this speaker
    const speakerAverage =
      speakerHistory.reduce((sum: number, rate: number) => sum + rate, 0) /
      speakerHistory.length;
    this.speakerAverages.set(speakerLabel, speakerAverage);

    this.prevEndTime = new Date(
      startTime.getTime() + this.playbackDuration * 1000,
    );

    if (excessLatency > 10) {
      console.log("Excess latency detected:", excessLatency);
      // TODO: Report this to sentry
    }
  }

  public calculatePlaybackRate(
    currentDuration: number,
    desiredDuration: number,
  ): number {
    const playbackRate = currentDuration / desiredDuration;
    return Math.max(
      this.minPlaybackRate,
      Math.min(this.maxPlaybackRate, playbackRate),
    );
  }

  public getPrevEndTime(): Date {
    return this.prevEndTime;
  }
}
